import React from 'react';
import { FadeIn } from './FadeIn';
import { motion } from 'framer-motion';
import { BookOpen, MessageSquare, ClipboardList, Repeat, AlertTriangle, Settings, Check } from 'lucide-react';
const tomos = [{
  num: 1,
  icon: <BookOpen size={20} data-id="element-227" />,
  title: 'Consulta Nutricional sin Improvisar',
  hook: 'Sentite seguro desde el primer minuto de cada consulta.',
  bullets: ['Estructura paso a paso para la primera visita', 'Criterios claros para evaluar y decidir', 'Checklist anti-improvisación']
}, {
  num: 2,
  icon: <MessageSquare size={20} data-id="element-228" />,
  title: 'Cómo Explicar, Indicar y Adaptar',
  hook: 'Que el paciente entienda, se comprometa y vuelva.',
  bullets: ['Guiones listos para cada momento de la consulta', 'Matrices para adaptar indicaciones en minutos', 'Recursos visuales para mejorar la adherencia']
}, {
  num: 3,
  icon: <ClipboardList size={20} data-id="element-229" />,
  title: 'Planificación Nutricional Lista para Usar',
  hook: 'Dejá de armar planes desde cero. Personalizá en 15 minutos.',
  bullets: ['Menús editables por objetivo y patología', 'Plantillas profesionales para entregar', 'Listas de equivalencias y reemplazos']
}, {
  num: 4,
  icon: <Repeat size={20} data-id="element-230" />,
  title: 'Seguimiento y Adherencia sin Complicarte',
  hook: 'Convertí pacientes esporádicos en pacientes que cumplen.',
  bullets: ['Protocolos de seguimiento por etapa', 'Herramientas de adherencia probadas', 'Sistema para reducir abandono']
}, {
  num: 5,
  icon: <AlertTriangle size={20} data-id="element-231" />,
  title: 'Pacientes que No Avanzan: Qué Revisar Primero',
  hook: 'Recuperá el control de los casos que te traban la agenda.',
  bullets: ['Árboles de decisión paso a paso', 'Guiones para conversaciones difíciles', 'Checklist de causas frecuentes de estancamiento']
}, {
  num: 6,
  icon: <Settings size={20} data-id="element-232" />,
  title: 'Sistema y Herramientas del Consultorio',
  hook: 'Profesionalizá tu consultorio como una verdadera empresa.',
  bullets: ['Flujos de trabajo y automatizaciones simples', 'Plantillas de gestión y administración', 'Criterios para escalar tu práctica']
}];
export function Tomos() {
  return <section className="relative py-12 md:py-16 overflow-hidden" data-id="element-233">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none opacity-60" style={{
      background: 'radial-gradient(ellipse at 50% 0%, rgba(74,222,128,0.10) 0%, transparent 60%)'
    }} data-id="element-234" />

      <div className="relative max-w-5xl mx-auto px-4" data-id="element-235">
        {/* Section header */}
        <FadeIn className="text-center mb-10" data-id="element-236">
          <span className="inline-block py-1 px-3 rounded-full bg-mint/10 text-mint font-semibold text-xs mb-3 tracking-widest uppercase border border-mint/20" data-id="element-237">
            6 Tomos · Contenido completo
          </span>
          <h2 className="font-['Open_Sans',_sans-serif] font-[900] text-3xl md:text-4xl text-dark leading-tight" data-id="element-238">
            Todo el sistema, paso a paso,{' '}
            <span className="text-mint" data-id="element-239">listo para aplicar hoy</span>
          </h2>
          <p className="text-dark/60 text-sm md:text-base mt-3 max-w-2xl mx-auto" data-id="element-240">
            Cada tomo resuelve una etapa concreta de la consulta. Sin teoría
            interminable. Solo lo que vas a usar.
          </p>
        </FadeIn>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-5" data-id="element-241">
          {tomos.map((tomo, idx) => <FadeIn key={idx} delay={idx * 0.08} data-id="element-242">
              <motion.div className="group relative bg-surface rounded-2xl border border-mint/15 h-full flex flex-col shadow-lg shadow-black/40 hover:border-mint/50 transition-all overflow-hidden" whileHover={{
            y: -4,
            boxShadow: '0 20px 40px -15px rgba(74,222,128,0.25)'
          }} transition={{
            duration: 0.3,
            ease: 'easeOut'
          }} data-id="element-243">
                {/* Top gradient ribbon */}
                <div className="h-1 w-full bg-gradient-to-r from-mint via-secondary to-mint/40" data-id="element-244" />

                {/* Decorative number watermark */}
                <span aria-hidden className="absolute -top-4 -right-2 text-[7rem] font-[900] font-['Open_Sans',_sans-serif] text-mint/[0.06] leading-none select-none pointer-events-none" data-id="element-245">
                  {String(tomo.num).padStart(2, '0')}
                </span>

                <div className="relative p-5 md:p-6 flex flex-col h-full" data-id="element-246">
                  {/* Header row */}
                  <div className="flex items-center gap-3 mb-4" data-id="element-247">
                    <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-mint/20 to-mint/5 border border-mint/30 flex items-center justify-center text-mint shadow-inner" data-id="element-248">
                      {tomo.icon}
                    </div>
                    <div data-id="element-249">
                      <p className="text-mint/80 text-[10px] font-bold tracking-[0.2em] uppercase" data-id="element-250">
                        Tomo {String(tomo.num).padStart(2, '0')}
                      </p>
                      <h3 className="font-['Open_Sans',_sans-serif] font-[800] text-lg md:text-xl text-dark leading-snug group-hover:text-mint transition-colors" data-id="element-251">
                        {tomo.title}
                      </h3>
                    </div>
                  </div>

                  {/* Marketing hook */}
                  <p className="text-mint italic text-sm md:text-base font-semibold mb-4 border-l-2 border-mint/40 pl-3" data-id="element-252">
                    {tomo.hook}
                  </p>

                  {/* Bullets */}
                  <ul className="space-y-2 mb-2" data-id="element-253">
                    {tomo.bullets.map((b, i) => <li key={i} className="flex items-start gap-2.5 text-dark/80 text-sm leading-relaxed" data-id="element-254">
                        <span className="shrink-0 mt-0.5 w-4 h-4 rounded-full bg-mint/15 border border-mint/30 flex items-center justify-center" data-id="element-255">
                          <Check size={10} strokeWidth={3} className="text-mint" data-id="element-256" />
                        </span>
                        <span data-id="element-257">{b}</span>
                      </li>)}
                  </ul>
                </div>
              </motion.div>
            </FadeIn>)}
        </div>

        {/* Bottom badge */}
        <FadeIn delay={0.6} className="text-center mt-8" data-id="element-258">
          <div className="inline-flex items-center gap-3 bg-surface border border-mint/20 rounded-full px-6 py-3 shadow-lg shadow-black/40" data-id="element-259">
            <span className="text-lg" data-id="element-260">📦</span>
            <p className="text-dark/80 text-sm font-medium" data-id="element-261">
              +600 páginas de contenido profesional listo para usar en consulta
            </p>
          </div>
        </FadeIn>
      </div>
    </section>;
}